import { forwardRef } from "react";

import { Link } from "react-router-dom";

import { cn } from "@btnet/utilities-core";

import type { IBuiLinkProps } from "./BuiLink.types";

/**
 * BuiLink's base Tailwind classes (everything except the conditional `underline`
 * and any caller `className`). Exported so components that must render a plain
 * `<a>` instead of a router `Link` — e.g. BTLink's tel/sms/external paths — can
 * apply identical styling without duplicating the string and drifting out of sync.
 */
export const BUI_LINK_CLASSES = cn(
  "bui-link font-sans text-sm font-normal focus-visible:rounded-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-50",
  "cursor-pointer text-fg-link visited:text-fg-link-visited hover:text-fg-link-hover active:text-fg-link-pressed",
);

/**
 * BuiLink is a thin Blueprint wrapper around react-router-dom's `Link`. It
 * exposes the exact same props as `Link`, applies the default Blueprint link
 * styling via Tailwind utilities (keyed off the BDS link tokens), forwards a
 * ref to the underlying anchor, and is intended to be the base for higher-level
 * link components such as BTLink.
 * @param props - react-router-dom `LinkProps` plus an optional `data-testid`
 * @param ref - Forwarded ref to the underlying `<a>` element
 * @returns The rendered router `Link`
 * @example
 * ```tsx
 * <BuiLink to="/jobs" data-testid="jobs-link">Jobs</BuiLink>
 * ```
 */
export const BuiLink = forwardRef<HTMLAnchorElement, IBuiLinkProps>(
  ({ className, underline = true, ...props }, ref) => {
    return (
      <Link
        {...props}
        // react-router-dom v5's Link is a class component, so `ref` targets the
        // instance; `innerRef` forwards to the underlying <a> element.
        innerRef={ref}
        className={cn(BUI_LINK_CLASSES, { underline }, className)}
      />
    );
  },
);

BuiLink.displayName = "BuiLink";
